import React, { useState } from 'react';
import {
  UserPlusIcon,
  BuildingOfficeIcon,
  TrashIcon,
  PencilIcon,
  EyeIcon,
  EyeSlashIcon,
  ShieldCheckIcon,
  UserIcon,
  XMarkIcon,
} from '@heroicons/react/24/outline';
import { User, Company } from '../types';
import { apiService } from '../services/apiService';
import { toast } from 'sonner';

interface UserManagementProps {
  users: User[];
  companies: Company[];
  currentUser: User | null;
  currentCompany?: Company | null;
  onClose: () => void;
  onCreateUser: (userData: Omit<User, 'id' | 'createdAt' | 'updatedAt'>) => void;
  onCreateCompany: (companyData: Omit<Company, 'id' | 'createdAt' | 'updatedAt'>) => void;
  onUpdateUser: (userId: string, userData: Partial<User>) => void;
  onUpdateCompany: (companyId: string, companyData: Partial<Company>) => void;
  onDeleteUser: (userId: string) => void;
  onDeleteCompany: (companyId: string) => void;
  onSwitchCompany: (companyId: string) => void;
  onOpenAISettings: () => void;
  onExportBackup: () => void;
  onImportBackup: (file: File) => void;
  variant?: 'modal' | 'inline';
}

export const UserManagement: React.FC<UserManagementProps> = ({
  users,
  companies,
  currentUser,
  currentCompany,
  onClose,
  onCreateUser,
  onCreateCompany,
  onUpdateUser,
  onUpdateCompany,
  onDeleteUser,
  onDeleteCompany,
  onSwitchCompany,
  onOpenAISettings,
  onExportBackup,
  onImportBackup,
  variant = 'modal'
}) => {
  const [activeTab, setActiveTab] = useState<'users' | 'companies'>('users');
  const [showUserForm, setShowUserForm] = useState(false);
  const [showCompanyForm, setShowCompanyForm] = useState(false);
  const [editingUser, setEditingUser] = useState<User | null>(null);
  const [editingCompany, setEditingCompany] = useState<Company | null>(null);

  // User form state
  const [userForm, setUserForm] = useState({
    name: '',
    email: '',
    password: '',
    role: 'user' as 'superadmin' | 'admin' | 'user' | 'usuario',
    companyId: currentCompany?.id || '',
    authorizedCompanyIds: [] as string[],
    isActive: true
  });

  // Company form state
  const [companyForm, setCompanyForm] = useState({
    name: '',
    cif: '',
    address: '',
    city: '',
    postalCode: '',
    email: '',
    phone: '',
    website: '',
    isActive: true,
    // Configuración de facturación
    invoicePrefix: 'FAC',
    currentYear: new Date().getFullYear(),
    lastInvoiceNumber: 0,
    fiscalYearStart: 1,
    logo: ''
  });

  const resetUserForm = () => {
    setUserForm({
      name: '',
      email: '',
      password: '',
      role: 'user',
      companyId: currentCompany?.id || '',
      authorizedCompanyIds: [],
      isActive: true
    });
    setEditingUser(null);
    setShowUserForm(false);
  };

  const resetCompanyForm = () => {
    setCompanyForm({
      name: '',
      cif: '',
      address: '',
      city: '',
      postalCode: '',
      email: '',
      phone: '',
      website: '',
      isActive: true,
      // Configuración de facturación
      invoicePrefix: 'FAC',
      currentYear: new Date().getFullYear(),
      lastInvoiceNumber: 0,
      fiscalYearStart: 1,
      logo: ''
    });
    setEditingCompany(null);
    setShowCompanyForm(false);
  };

  const handleUserSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    
    if (editingUser) {
      onUpdateUser(editingUser.id, {
        ...userForm,
        updatedAt: new Date()
      });
    } else {
      onCreateUser({
        ...userForm,
        lastLogin: undefined
      });
    }
    
    resetUserForm();
  };

  const handleCompanySubmit = (e: React.FormEvent) => {
    e.preventDefault();
    
    if (editingCompany) {
      onUpdateCompany(editingCompany.id, {
        ...companyForm,
        updatedAt: new Date()
      });
    } else {
      onCreateCompany(companyForm);
    }
    
    resetCompanyForm();
  };

  const handleEditUser = (user: User) => {
    setUserForm({
      name: user.name,
      email: user.email,
      password: '', // Don't pre-fill password for security
      role: user.role,
      companyId: user.companyId,
      authorizedCompanyIds: user.authorizedCompanyIds || [],
      isActive: user.isActive
    });
    setEditingUser(user);
    setShowUserForm(true);
  };

  const handleEditCompany = (company: Company) => {
    setCompanyForm({
      name: company.name,
      cif: company.cif,
      address: company.address,
      city: company.city,
      postalCode: company.postalCode,
      email: company.email,
      phone: company.phone || '',
      website: company.website || '',
      isActive: company.isActive,
      // Configuración de facturación
      invoicePrefix: company.invoicePrefix || 'FAC',
      currentYear: company.currentYear || new Date().getFullYear(),
      lastInvoiceNumber: company.lastInvoiceNumber || 0,
      fiscalYearStart: company.fiscalYearStart || 1,
      logo: company.logo || ''
    });
    setEditingCompany(company);
    setShowCompanyForm(true);
  };

  const isPrivileged = currentUser?.role === 'admin' || currentUser?.role === 'superadmin';
  const companyUsers = users.filter((user) => user.role !== 'usuario');
  const filteredUsers = (isPrivileged || !currentCompany)
    ? companyUsers
    : companyUsers.filter((user) => user.companyId === currentCompany.id);

  const filteredCompanies = isPrivileged
    ? companies
    : (!currentCompany
        ? companies
        : companies.filter(company => (
            currentUser?.authorizedCompanyIds?.includes(company.id) || company.id === currentCompany.id
          )));

  return (
    <div className={`${variant === 'modal' ? 'fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50' : ''}`}>
      <div className="bg-white rounded-lg max-w-6xl w-full max-h-[90vh] overflow-hidden">
        {/* Modal Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-200">
          <div>
            <h2 className="text-2xl font-bold text-gray-900">Gestión de Usuarios y Empresas</h2>
            <p className="text-gray-600">Administra usuarios y empresas del sistema</p>
          </div>
          {variant === 'modal' && (
            <button
              onClick={onClose}
              className="text-gray-400 hover:text-gray-600 p-2 hover:bg-gray-100 rounded-md"
            >
              <XMarkIcon className="h-6 w-6" />
            </button>
          )}
        </div>

        {/* Modal Content */}
        <div className="p-6 overflow-y-auto max-h-[70vh] space-y-6">
        {/* Edit current company */}
        {(currentUser?.role === 'admin' || currentUser?.role === 'superadmin') && currentCompany && (
          <div className="flex justify-end mb-4">
            <button
              onClick={() => handleEditCompany(currentCompany)}
              className="px-3 py-2 text-sm bg-amber-500 text-white rounded-md hover:bg-amber-600"
            >
              Editar empresa actual
            </button>
          </div>
        )}
        
        {/* Company Switcher (only for admin/superadmin) */}
        {(currentUser?.role === 'admin' || currentUser?.role === 'superadmin') && companies.length > 1 && (
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-2">
              <label className="text-sm font-medium text-gray-700">Empresa actual:</label>
              <select
                value={currentCompany?.id || ''}
                onChange={(e) => onSwitchCompany(e.target.value)}
                className="border border-gray-300 rounded-md px-3 py-1 text-sm"
              >
                {companies.map(company => (
                  <option key={company.id} value={company.id}>
                    {company.name}
                  </option>
                ))}
              </select>
            </div>
            {currentCompany && (
              <button
                onClick={() => handleEditCompany(currentCompany)}
                className="px-3 py-1 text-sm bg-blue-500 text-white rounded-md hover:bg-blue-600 flex items-center space-x-1"
              >
                <PencilIcon className="h-3 w-3" />
                <span>Editar</span>
              </button>
            )}
          </div>
        )}
      </div>

      {/* Tabs */}
      <div className="border-b border-gray-200">
        <nav className="-mb-px flex space-x-8">
          <button
            onClick={() => setActiveTab('users')}
            className={`${
              activeTab === 'users'
                ? 'border-blue-500 text-blue-600'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
            } whitespace-nowrap py-2 px-1 border-b-2 font-medium text-sm transition-colors`}
          >
            <UserIcon className="h-5 w-5 inline mr-2" />
            Usuarios
          </button>
          <button
            onClick={() => setActiveTab('companies')}
            className={`${
              activeTab === 'companies'
                ? 'border-blue-500 text-blue-600'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
            } whitespace-nowrap py-2 px-1 border-b-2 font-medium text-sm transition-colors`}
          >
            <BuildingOfficeIcon className="h-5 w-5 inline mr-2" />
            Empresas
          </button>
        </nav>
      </div>

      {/* Users Tab */}
      {activeTab === 'users' && (
        <div className="space-y-6">
          {/* Add User Button (only for superadmin) */}
          {currentUser?.role === 'superadmin' && (
            <div className="flex justify-end">
              <button
                onClick={() => setShowUserForm(true)}
                className="bg-blue-600 text-white px-4 py-2 rounded-md hover:bg-blue-700 flex items-center space-x-2"
              >
                <UserPlusIcon className="h-5 w-5" />
                <span>Nuevo Usuario</span>
              </button>
            </div>
          )}

          {/* Users List (vertical) */}
          <div className="bg-white shadow overflow-hidden sm:rounded-md">
            <ul className="divide-y divide-gray-200">
              {filteredUsers.map((user) => (
                <li key={user.id} className="px-6 py-4">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center space-x-4">
                      <div className={`p-2 rounded-full ${user.isActive ? 'bg-green-100' : 'bg-gray-100'}` }>
                        <UserIcon className={`h-6 w-6 ${user.isActive ? 'text-green-600' : 'text-gray-400'}`} />
                      </div>
                      <div>
                        <div className="flex items-center space-x-2">
                          <p className="text-sm font-medium text-gray-900">{user.name}</p>
                          {(user.role === 'admin' || user.role === 'superadmin') && (
                            <ShieldCheckIcon className="h-4 w-4 text-blue-500" title={user.role === 'superadmin' ? 'Superadmin' : 'Administrador'} />
                          )}
                          {!user.isActive && (
                            <span className="px-2 py-1 text-xs bg-red-100 text-red-800 rounded-full">Inactivo</span>
                          )}
                        </div>
                        <p className="text-sm text-gray-500">{user.email}</p>
                        <p className="text-xs text-gray-400">Empresa: {companies.find(c => c.id === user.companyId)?.name || 'Sin asignar'}</p>
                        {user.lastLogin && (
                          <p className="text-xs text-gray-400">Último acceso: {user.lastLogin.toLocaleString('es-ES')}</p>
                        )}
                      </div>
                    </div>
                    <div className="flex items-center space-x-2">
                      <button
                        onClick={() => handleEditUser(user)}
                        className="p-2 text-blue-400 hover:text-blue-600 hover:bg-blue-50 rounded-md"
                        title="Editar usuario"
                      >
                        <PencilIcon className="h-4 w-4" />
                      </button>
                      <button
                        onClick={() => onUpdateUser(user.id, { isActive: !user.isActive })}
                        className={`p-2 rounded-md ${user.isActive ? 'text-yellow-400 hover:text-yellow-600 hover:bg-yellow-50' : 'text-green-400 hover:text-green-600 hover:bg-green-50'}`}
                        title={user.isActive ? 'Desactivar usuario' : 'Activar usuario'}
                      >
                        {user.isActive ? <EyeSlashIcon className="h-4 w-4" /> : <EyeIcon className="h-4 w-4" />}
                      </button>
                      {(currentUser?.role === 'admin' || currentUser?.role === 'superadmin') && user.id !== currentUser.id && (
                        <button
                          onClick={() => onDeleteUser(user.id)}
                          className="p-2 text-red-400 hover:text-red-600 hover:bg-red-50 rounded-md"
                          title="Eliminar usuario"
                        >
                          <TrashIcon className="h-4 w-4" />
                        </button>
                      )}
                    </div>
                  </div>
                </li>
              ))}
            </ul>
          </div>
        </div>
      )}

      {/* Companies Tab */}
      {activeTab === 'companies' && (
        <div className="space-y-6">
          {/* Add Company Button (only for superadmin) */}
          {currentUser?.role === 'superadmin' && (
            <div className="flex justify-end">
              <button
                onClick={() => setShowCompanyForm(true)}
                className="bg-green-600 text-white px-4 py-2 rounded-md hover:bg-green-700 flex items-center space-x-2"
              >
                <BuildingOfficeIcon className="h-5 w-5" />
                <span>Nueva Empresa</span>
              </button>
            </div>
          )}

          {/* Companies List (one per row, horizontal content) */}
          <div className="space-y-3">
            {filteredCompanies.map((company) => (
              <div key={company.id} className="bg-white shadow rounded-lg p-4">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    {company.logo ? (
                      <img
                        src={company.logo}
                        alt={`Logo de ${company.name}`}
                        className="w-12 h-12 object-contain rounded border border-gray-200"
                        onError={(e) => { (e.currentTarget as HTMLImageElement).style.display = 'none'; }}
                      />
                    ) : (
                      <div className="flex items-center justify-center w-12 h-12 bg-gray-100 rounded border border-gray-200">
                        <BuildingOfficeIcon className="h-6 w-6 text-gray-400" />
                      </div>
                    )}
                    <div>
                      <div className="flex items-center gap-2">
                        <h3 className="text-base font-medium text-gray-900">{company.name}</h3>
                        {!company.isActive && (
                          <span className="px-2 py-0.5 text-xs bg-red-100 text-red-700 rounded-full">Inactiva</span>
                        )}
                      </div>
                      <div className="text-sm text-gray-500 flex flex-wrap gap-x-4">
                        <span>CIF: {company.cif}</span>
                        {company.email && <span>{company.email}</span>}
                        {company.city && <span>{company.city}</span>}
                      </div>
                      <div className="mt-1 text-xs text-gray-500 flex flex-wrap gap-x-4">
                        <span>Prefijo: {company.invoicePrefix || 'FAC'}</span>
                        <span>Año: {company.currentYear || new Date().getFullYear()}</span>
                        <span>Última: {company.lastInvoiceNumber || 0}</span>
                        <span>Formato: {(company.invoicePrefix || 'FAC')}-{company.currentYear || new Date().getFullYear()}-{String((company.lastInvoiceNumber || 0) + 1)}</span>
                      </div>
                    </div>
                  </div>
                  {(currentUser?.role === 'admin' || currentUser?.role === 'superadmin') && (
                    <div className="flex items-center gap-2">
                      <button
                        onClick={() => handleEditCompany(company)}
                        className="px-3 py-1.5 text-sm bg-blue-600 text-white rounded-md hover:bg-blue-700"
                        title="Editar empresa"
                      >
                        Editar
                      </button>
                      <button
                        onClick={() => onUpdateCompany(company.id, { isActive: !company.isActive })}
                        className={`px-3 py-1.5 text-sm rounded-md ${company.isActive ? 'bg-yellow-100 text-yellow-700 hover:bg-yellow-200' : 'bg-green-100 text-green-700 hover:bg-green-200'}`}
                        title={company.isActive ? 'Desactivar empresa' : 'Activar empresa'}
                      >
                        {company.isActive ? 'Desactivar' : 'Activar'}
                      </button>
                      {company.id !== currentCompany?.id && (
                        <button
                          onClick={() => {
                            if (!window.confirm(`¿Seguro que quieres eliminar la empresa "${company.name}"?`)) return;
                            const check = window.prompt('Escribe ELIMINAR para confirmar:');
                            if (check === 'ELIMINAR') onDeleteCompany(company.id);
                          }}
                          className="px-3 py-1.5 text-sm bg-red-100 text-red-700 rounded-md hover:bg-red-200"
                          title="Eliminar empresa"
                        >
                          Borrar
                        </button>
                      )}
                    </div>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* User Form Modal */}
      {showUserForm && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
          <div className="relative top-20 mx-auto p-5 border w-96 shadow-lg rounded-md bg-white">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-medium text-gray-900">
                {editingUser ? 'Editar Usuario' : 'Nuevo Usuario'}
              </h3>
              <button
                onClick={resetUserForm}
                className="text-gray-400 hover:text-gray-600"
              >
                <XMarkIcon className="h-6 w-6" />
              </button>
            </div>
            
            <form onSubmit={handleUserSubmit} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700">Nombre</label>
                <input
                  type="text"
                  required
                  value={userForm.name}
                  onChange={(e) => setUserForm({ ...userForm, name: e.target.value })}
                  className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2"
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700">Email</label>
                <input
                  type="email"
                  required
                  value={userForm.email}
                  onChange={(e) => setUserForm({ ...userForm, email: e.target.value })}
                  className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2"
                />
              </div>

              {/* Authorized companies (superadmin only) */}
              {currentUser?.role === 'superadmin' && (
                <div>
                  <label className="block text-sm font-medium text-gray-700">Empresas autorizadas</label>
                  <select
                    multiple
                    value={userForm.authorizedCompanyIds || []}
                    onChange={(e) => {
                      const options = Array.from(e.target.selectedOptions).map(o => o.value);
                      setUserForm({ ...userForm, authorizedCompanyIds: options });
                    }}
                    className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2 h-28"
                  >
                    {companies.map(c => (
                      <option key={c.id} value={c.id}>{c.name}</option>
                    ))}
                  </select>
                  <p className="mt-1 text-xs text-gray-500">Selecciona las empresas a las que este usuario puede acceder en su panel.</p>
                </div>
              )}
              
              <div>
                <label className="block text-sm font-medium text-gray-700">
                  {editingUser ? 'Nueva Contraseña (opcional)' : 'Contraseña'}
                </label>
                <input
                  type="password"
                  required={!editingUser}
                  value={userForm.password}
                  onChange={(e) => setUserForm({ ...userForm, password: e.target.value })}
                  className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2"
                  placeholder={editingUser ? 'Dejar vacío para mantener actual' : 'Contraseña del usuario'}
                />
                {!editingUser && (
                  <p className="mt-1 text-xs text-gray-500">
                    Mínimo 6 caracteres con mayúscula, minúscula y número
                  </p>
                )}
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700">Rol</label>
                <select
                  value={userForm.role}
                  onChange={(e) => setUserForm({ ...userForm, role: e.target.value as 'admin' | 'user' })}
                  className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2"
                >
                  <option value="user">Usuario</option>
                  <option value="admin">Administrador</option>
                </select>
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700">Empresa</label>
                <select
                  value={userForm.companyId}
                  onChange={(e) => setUserForm({ ...userForm, companyId: e.target.value })}
                  className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2"
                  required
                >
                  <option value="">Seleccionar empresa</option>
                  {companies.map(company => (
                    <option key={company.id} value={company.id}>
                      {company.name}
                    </option>
                  ))}
                </select>
              </div>
              
              <div className="flex items-center">
                <input
                  type="checkbox"
                  id="userActive"
                  checked={userForm.isActive}
                  onChange={(e) => setUserForm({ ...userForm, isActive: e.target.checked })}
                  className="h-4 w-4 text-blue-600 border-gray-300 rounded"
                />
                <label htmlFor="userActive" className="ml-2 block text-sm text-gray-900">
                  Usuario activo
                </label>
              </div>
              
              <div className="flex justify-end space-x-3 pt-4">
                <button
                  type="button"
                  onClick={resetUserForm}
                  className="px-4 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50"
                >
                  Cancelar
                </button>
                <button
                  type="submit"
                  className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700"
                >
                  {editingUser ? 'Actualizar' : 'Crear'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Company Form Modal */}
      {showCompanyForm && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
          <div className="relative top-10 mx-auto p-5 border w-[600px] shadow-lg rounded-md bg-white">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-medium text-gray-900">
                {editingCompany ? 'Editar Empresa' : 'Nueva Empresa'}
              </h3>
              <button
                onClick={resetCompanyForm}
                className="text-gray-400 hover:text-gray-600"
              >
                <XMarkIcon className="h-6 w-6" />
              </button>
            </div>
            
            <form onSubmit={handleCompanySubmit} className="space-y-4 max-h-96 overflow-y-auto">
              <div>
                <label className="block text-sm font-medium text-gray-700">Nombre de la empresa</label>
                <input
                  type="text"
                  required
                  value={companyForm.name}
                  onChange={(e) => setCompanyForm({ ...companyForm, name: e.target.value })}
                  className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2"
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700">Logo de la empresa (URL)</label>
                <input
                  type="url"
                  value={companyForm.logo || ''}
                  onChange={(e) => setCompanyForm({ ...companyForm, logo: e.target.value })}
                  className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2"
                  placeholder="https://ejemplo.com/logo.png"
                />
                {editingCompany && (
                  <div className="mt-2">
                    <label className="block text-sm font-medium text-gray-700">o subir archivo</label>
                    <input
                      type="file"
                      accept="image/*"
                      onChange={async (e) => {
                        const file = e.target.files?.[0];
                        if (!file || !editingCompany) return;
                        const resp = await apiService.uploadCompanyLogo(editingCompany.id, file);
                        if ((resp as any).error) {
                          toast.error((resp as any).error);
                          return;
                        }
                        const url = (resp.data as any)?.logoUrl;
                        if (url) {
                          setCompanyForm((prev) => ({ ...prev, logo: url }));
                          // Persistir en el estado global refrescando la empresa
                          onUpdateCompany(editingCompany.id, { logo: url });
                        }
                      }}
                      className="mt-1 block w-full text-sm text-gray-700"
                    />
                    <p className="mt-1 text-xs text-gray-500">Se guardará y mostrará automáticamente.</p>
                  </div>
                )}
                {companyForm.logo && (
                  <div className="mt-2 flex items-center space-x-3">
                    <img 
                      src={companyForm.logo} 
                      alt="Vista previa del logo"
                      className="w-12 h-12 object-contain border border-gray-200 rounded"
                      onError={(e) => {
                        e.currentTarget.style.display = 'none';
                      }}
                    />
                    <span className="text-xs text-gray-500">Vista previa</span>
                  </div>
                )}
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700">CIF</label>
                <input
                  type="text"
                  required
                  value={companyForm.cif}
                  onChange={(e) => setCompanyForm({ ...companyForm, cif: e.target.value })}
                  className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2"
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700">Dirección</label>
                <input
                  type="text"
                  required
                  value={companyForm.address}
                  onChange={(e) => setCompanyForm({ ...companyForm, address: e.target.value })}
                  className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2"
                />
              </div>
              
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-sm font-medium text-gray-700">Ciudad</label>
                  <input
                    type="text"
                    required
                    value={companyForm.city}
                    onChange={(e) => setCompanyForm({ ...companyForm, city: e.target.value })}
                    className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700">Código Postal</label>
                  <input
                    type="text"
                    required
                    value={companyForm.postalCode}
                    onChange={(e) => setCompanyForm({ ...companyForm, postalCode: e.target.value })}
                    className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2"
                  />
                </div>
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700">Email</label>
                <input
                  type="email"
                  required
                  value={companyForm.email}
                  onChange={(e) => setCompanyForm({ ...companyForm, email: e.target.value })}
                  className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2"
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700">Teléfono</label>
                <input
                  type="tel"
                  value={companyForm.phone}
                  onChange={(e) => setCompanyForm({ ...companyForm, phone: e.target.value })}
                  className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2"
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700">Sitio web</label>
                <input
                  type="url"
                  value={companyForm.website}
                  onChange={(e) => setCompanyForm({ ...companyForm, website: e.target.value })}
                  className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2"
                />
              </div>
              
              {/* Configuración de Facturación */}
              <div className="border-t border-gray-200 pt-4">
                <h4 className="text-sm font-medium text-gray-700 mb-3">Configuración de Facturación</h4>
                
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="block text-sm font-medium text-gray-700">Prefijo de Facturas</label>
                    <input
                      type="text"
                      value={companyForm.invoicePrefix}
                      onChange={(e) => setCompanyForm({ ...companyForm, invoicePrefix: e.target.value })}
                      className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2"
                      placeholder="FAC"
                    />
                    <p className="text-xs text-gray-500 mt-1">Ej: FAC, INV, BILL</p>
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-gray-700">Año Actual</label>
                    <input
                      type="number"
                      value={companyForm.currentYear}
                      onChange={(e) => setCompanyForm({ ...companyForm, currentYear: parseInt(e.target.value) || new Date().getFullYear() })}
                      className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2"
                      min="2020"
                      max="2030"
                    />
                  </div>
                </div>
                
                <div className="grid grid-cols-2 gap-3 mt-3">
                  <div>
                    <label className="block text-sm font-medium text-gray-700">Último Número</label>
                    <input
                      type="number"
                      value={companyForm.lastInvoiceNumber}
                      onChange={(e) => setCompanyForm({ ...companyForm, lastInvoiceNumber: parseInt(e.target.value) || 0 })}
                      className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2"
                      min="0"
                    />
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-gray-700">Mes Inicio Fiscal</label>
                    <select
                      value={companyForm.fiscalYearStart}
                      onChange={(e) => setCompanyForm({ ...companyForm, fiscalYearStart: parseInt(e.target.value) })}
                      className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2"
                    >
                      <option value={1}>Enero</option>
                      <option value={2}>Febrero</option>
                      <option value={3}>Marzo</option>
                      <option value={4}>Abril</option>
                      <option value={5}>Mayo</option>
                      <option value={6}>Junio</option>
                      <option value={7}>Julio</option>
                      <option value={8}>Agosto</option>
                      <option value={9}>Septiembre</option>
                      <option value={10}>Octubre</option>
                      <option value={11}>Noviembre</option>
                      <option value={12}>Diciembre</option>
                    </select>
                  </div>
                </div>
                
                <div className="mt-3 bg-blue-50 p-3 rounded-md">
                  <p className="text-sm text-blue-700">
                    <strong>Formato de factura:</strong> {companyForm.invoicePrefix}-{companyForm.currentYear}-{String(companyForm.lastInvoiceNumber + 1).padStart(3, '0')}
                  </p>
                  <p className="text-xs text-blue-600 mt-1">
                    Ejemplo: {companyForm.invoicePrefix}-{companyForm.currentYear}-001
                  </p>
                </div>
              </div>
              
              {/* Autorización de Usuarios */}
              <div className="border-t border-gray-200 pt-4">
                <h4 className="text-sm font-medium text-gray-700 mb-3">Autorización de Usuarios</h4>
                <p className="text-xs text-gray-500 mb-3">
                  Selecciona qué usuarios pueden acceder a esta empresa
                </p>
                
                <div className="space-y-2 max-h-40 overflow-y-auto border border-gray-200 rounded-md p-3 bg-gray-50">
                  {users.map((user) => (
                    <div key={user.id} className="flex items-center space-x-3">
                      <input
                        type="checkbox"
                        id={`user_${user.id}`}
                        checked={user.authorizedCompanyIds?.includes(editingCompany?.id || '') || user.companyId === editingCompany?.id}
                        onChange={(e) => {
                          if (e.target.checked) {
                            // Agregar empresa a las autorizadas del usuario
                            const updatedUser = {
                              ...user,
                              authorizedCompanyIds: [...(user.authorizedCompanyIds || []), editingCompany?.id || '']
                            };
                            onUpdateUser(user.id, updatedUser);
                          } else {
                            // Remover empresa de las autorizadas del usuario
                            const updatedUser = {
                              ...user,
                              authorizedCompanyIds: (user.authorizedCompanyIds || []).filter(id => id !== editingCompany?.id)
                            };
                            onUpdateUser(user.id, updatedUser);
                          }
                        }}
                        className="h-4 w-4 text-blue-600 border-gray-300 rounded"
                        disabled={user.companyId === editingCompany?.id} // No se puede desautorizar al usuario principal
                      />
                      <label htmlFor={`user_${user.id}`} className="flex-1 text-sm text-gray-700">
                        <div className="flex items-center justify-between">
                          <span className="font-medium">{user.name}</span>
                          <span className="text-xs text-gray-500">
                            {user.companyId === editingCompany?.id ? '(Principal)' : user.role}
                          </span>
                        </div>
                        <div className="text-xs text-gray-500">{user.email}</div>
                      </label>
                    </div>
                  ))}
                </div>
                
                {editingCompany && (
                  <div className="mt-2 text-xs text-gray-500">
                    <p><strong>Usuarios autorizados:</strong> {users.filter(u => 
                      u.authorizedCompanyIds?.includes(editingCompany.id) || u.companyId === editingCompany.id
                    ).length}</p>
                    <p><strong>Usuario principal:</strong> {users.find(u => u.companyId === editingCompany.id)?.name || 'No asignado'}</p>
                  </div>
                )}
              </div>
              
              <div className="flex items-center">
                <input
                  type="checkbox"
                  id="companyActive"
                  checked={companyForm.isActive}
                  onChange={(e) => setCompanyForm({ ...companyForm, isActive: e.target.checked })}
                  className="h-4 w-4 text-blue-600 border-gray-300 rounded"
                />
                <label htmlFor="companyActive" className="ml-2 block text-sm text-gray-900">
                  Empresa activa
                </label>
              </div>
              
              <div className="flex justify-end space-x-3 pt-4">
                <button
                  type="button"
                  onClick={resetCompanyForm}
                  className="px-4 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50"
                >
                  Cancelar
                </button>
                <button
                  type="submit"
                  className="px-4 py-2 bg-green-600 text-white rounded-md hover:bg-green-700"
                >
                  {editingCompany ? 'Actualizar' : 'Crear'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
      </div>
    </div>
  );
}; 